'use client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { useOptions } from '@/lib/api/common/options.queries';
import {
  useActiveProfessions,
  useGetTopCountWorks,
} from '@/features/jobs/api/jobs.queries';
import { POST_DATE_OPTIONS } from '@/features/jobs/components/sidebar/PostDateComponent';
import {
  getOptionLabel,
  SidebarOption,
} from '@/features/jobs/components/sidebar/sidebar.utils';
import { IOptions } from '@/shared';

type SelectedTag = {
  id: string;
  type: string;
  label: string;
};

const findSelectedOptions = (
  selectedValues: string[],
  options: SidebarOption[],
  type: string
): SelectedTag[] => {
  if (!selectedValues.length || !options.length) return [];

  const tags: SelectedTag[] = [];

  const pushIfMatch = (option: SidebarOption) => {
    const id = String(option.id);
    if (!selectedValues.includes(id)) return;
    tags.push({
      id,
      type,
      label: getOptionLabel(option),
    });
  };

  for (const option of options) {
    pushIfMatch(option);
    (option.children ?? []).forEach(pushIfMatch);
    (option.professions ?? []).forEach(pushIfMatch);
  }

  return tags;
};

export default function SearchTags() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const { data: workTypeOptions = [] } = useOptions({ gkey: 'worktime' });
  const { data: skillLevelOptions = [] } = useOptions({
    gkey: 'position_level',
  });
  const { data: locationOptions = [] } = useOptions({
    gkey: 'location',
    withChildren: true,
  });
  const { data: businessCategoryOptions = [] } = useOptions({
    gkey: 'branch',
  });
  const { data: topWorks } = useGetTopCountWorks();
  const branchId = Number(searchParams.get('branchId')) || 0;
  const { data: activeProfessions = [] } = useActiveProfessions(branchId);

  const professionOptions = useMemo(() => {
    const byId = new Map<number, IOptions>();

    for (const profession of activeProfessions) {
      byId.set(profession.id, profession);
    }

    for (const p of topWorks?.data ?? []) {
      if (!byId.has(p.id)) {
        byId.set(p.id, {
          id: p.id,
          name_mn: p.profession,
          name_en: p.profession_en ?? '',
        });
      }
      for (const s of p.profs_data ?? []) {
        if (!byId.has(s.id)) {
          byId.set(s.id, {
            id: s.id,
            name_mn: s.name,
            name_en: s.name,
          });
        }
      }
    }

    return [...byId.values()];
  }, [activeProfessions, topWorks]);

  const allSelectedOptions = useMemo(() => {
    const filters: { options: SidebarOption[]; param: string }[] = [
      { options: businessCategoryOptions, param: 'branchId' },
      { options: professionOptions, param: 'professionIds' },
      { options: workTypeOptions, param: 'timeType' },
      { options: skillLevelOptions, param: 'level' },
      { options: locationOptions, param: 'locationIds' },
      { options: POST_DATE_OPTIONS, param: 'dayAgo' },
    ];
    return filters.flatMap(({ options, param }) =>
      findSelectedOptions(
        searchParams.get(param)?.split(',').filter(Boolean) ?? [],
        options,
        param
      )
    );
  }, [
    searchParams,
    businessCategoryOptions,
    professionOptions,
    workTypeOptions,
    skillLevelOptions,
    locationOptions,
  ]);

  const removeOption = (id: string, type: string) => {
    const params = new URLSearchParams(searchParams.toString());
    const updatedValues = (params.get(type)?.split(',') || []).filter(
      (value) => value !== id
    );

    if (updatedValues.length > 0) {
      params.set(type, updatedValues.join(','));
    } else {
      params.delete(type);
    }

    router.push(`/job/list?${params.toString()}`);
  };

  if (allSelectedOptions.length === 0) return null;

  return (
    <div className="container mx-auto">
      <div className="flex gap-3 align-middle py-3 pl-2 w-full overflow-x-auto bg-white">
        <div className="flex-none">
          <div className="flex flex-wrap gap-3 py-1 ">
            {allSelectedOptions.map((option) => (
              <div
                className="text-xs bg-primary-50 text-[#003C77] px-2 py-1 rounded-full flex items-center gap-1"
                key={`${option.type}-${option.id}`}
              >
                {option.label}
                <button
                  onClick={() => removeOption(option.id, option.type)}
                  className="ml-1 hover:text-red-500"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
